// src/components/SiteManager.js - Updated with delete functionality
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import axios from 'axios';
import { apiService } from "../services/apiService";

const SiteManager = ({ sites, onSitesChange, loading }) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [selectedSites, setSelectedSites] = useState([]);
  const [operationStates, setOperationStates] = useState({});
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'single' | 'bulk', siteId?: string, siteIds?: string[], siteName?: string }
  
  const navigate = useNavigate();
  const { success, error, loading: showLoading, updateNotification } = useNotifications();

  // Filter and search sites
  const filteredSites = useMemo(() => {
    return sites.filter(site => {
      const matchesSearch = !searchTerm || 
        site.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        site.state_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || site.status === statusFilter;
      const matchesPlatform = platformFilter === 'all' || site.platform === platformFilter;
      
      return matchesSearch && matchesStatus && matchesPlatform;
    });
  }, [sites, searchTerm, statusFilter, platformFilter]);

  // Get unique statuses and platforms for filters
  const statuses = useMemo(() => {
    const uniqueStatuses = [...new Set(sites.map(site => site.status).filter(Boolean))];
    return uniqueStatuses.sort();
  }, [sites]);

  const platforms = useMemo(() => {
    const uniquePlatforms = [...new Set(sites.map(site => site.platform).filter(Boolean))];
    return uniquePlatforms.sort();
  }, [sites]);

  const setOperationState = (siteId, state) => {
    setOperationStates(prev => ({
      ...prev,
      [siteId]: state
    }));
  };

  // Delete functions
  const handleDeleteRequest = (site) => {
    setDeleteTarget({
      type: 'single',
      siteId: site.id,
      siteName: `${site.service} in ${site.city}, ${site.state_name}`
    });
    setShowDeleteModal(true);
  };

  const handleBulkDeleteRequest = () => {
    if (selectedSites.length === 0) {
      error('No sites selected for deletion');
      return;
    }

    const selectedSiteNames = selectedSites.map(siteId => {
      const site = sites.find(s => s.id === siteId);
      return site ? `${site.service} in ${site.city}` : 'Unknown site';
    });

    setDeleteTarget({
      type: 'bulk',
      siteIds: selectedSites,
      siteName: `${selectedSites.length} selected sites`,
      siteNames: selectedSiteNames
    });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const isDeleting = deleteTarget.type === 'single' ? deleteTarget.siteId : 'bulk';
    setOperationState(isDeleting, 'deleting');
    
    const loadingId = showLoading(
      deleteTarget.type === 'single' 
        ? 'Deleting site...' 
        : `Deleting ${deleteTarget.siteIds.length} sites...`
    );

    try {
      let result;
      
      if (deleteTarget.type === 'single') {
        result = await apiService.deleteSite(deleteTarget.siteId);
      } else {
        result = await apiService.deleteSites(deleteTarget.siteIds);
      }

      updateNotification(loadingId, {
        type: 'success',
        message: deleteTarget.type === 'single' 
          ? 'Site deleted successfully!' 
          : `${deleteTarget.siteIds.length} sites deleted successfully!`,
        duration: 5000
      });

      // Clear selections and refresh sites
      setSelectedSites([]);
      onSitesChange();
      
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: 'Delete failed: ' + err.message,
        duration: 7000
      });
    } finally {
      setOperationState(isDeleting, null);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  // Site selection functions
  const toggleSiteSelection = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const selectAllSites = () => {
    if (selectedSites.length === filteredSites.length) {
      setSelectedSites([]);
    } else {
      setSelectedSites(filteredSites.map(site => site.id));
    }
  };

  // Existing functions (regenerate, redeploy, deploy) remain the same...
  const handleRegenerate = async (siteId) => {
    setOperationState(siteId, 'regenerating');
    const loadingId = showLoading("Regenerating static site...");
    
    try {
      await apiService.regenerateSite(siteId);
      updateNotification(loadingId, {
        type: 'success',
        message: 'Static site regeneration started successfully!',
        duration: 5000
      });
      setTimeout(() => {
        onSitesChange();
        setOperationState(siteId, null);
      }, 2000);
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: 'Regeneration failed: ' + err.message,
        duration: 7000
      });
      setOperationState(siteId, null);
    }
  };

  const handleRedeploy = async (site) => {
    setOperationState(site.id, 'redeploying');
    const loadingId = showLoading(`Redeploying to ${site.platform.toUpperCase()}...`);
    
    try {
      await apiService.redeploySite(site.id, site.platform);
      updateNotification(loadingId, {
        type: 'success',
        message: `Redeploy to ${site.platform.toUpperCase()} started successfully!`,
        duration: 5000
      });
      setTimeout(() => {
        onSitesChange();
        setOperationState(site.id, null);
      }, 2000);
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: 'Redeploy failed: ' + err.message,
        duration: 7000
      });
      setOperationState(site.id, null);
    }
  };

  const handleDeploy = async (site, platform) => {
    setOperationState(site.id, 'deploying');
    const loadingId = showLoading(`Deploying to ${platform.toUpperCase()}...`);
    
    try {
      await apiService.deploySite(site.id, platform);
      updateNotification(loadingId, {
        type: 'success',
        message: `Deploy to ${platform.toUpperCase()} started successfully!`,
        duration: 5000
      });
      setTimeout(() => {
        onSitesChange();
        setOperationState(site.id, null);
      }, 2000);
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: `${platform.toUpperCase()} deploy failed: ` + err.message,
        duration: 7000
      });
      setOperationState(site.id, null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'deployed': return 'bg-green-100 text-green-700 border-green-200';
      case 'working': case 'generating': case 'deploying': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'error': return 'bg-red-100 text-red-700 border-red-200';
      case 'ready': case 'generated': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'deployed':
        return <div className="w-2 h-2 bg-green-400 rounded-full"></div>;
      case 'working': case 'generating': case 'deploying':
        return <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>;
      case 'error':
        return <div className="w-2 h-2 bg-red-400 rounded-full"></div>;
      default:
        return <div className="w-2 h-2 bg-gray-400 rounded-full"></div>;
    }
  };

  const SiteCard = ({ site }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-200 relative">
      {/* Selection checkbox */}
      <div className="absolute top-4 left-4">
        <input
          type="checkbox"
          checked={selectedSites.includes(site.id)}
          onChange={() => toggleSiteSelection(site.id)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
      </div>

      <div className="flex items-start justify-between mb-4 ml-8">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {getStatusIcon(site.status)}
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-lg text-gray-900 truncate">
              {site.service} in {site.city}
            </h3>
            <p className="text-sm text-gray-500">{site.city}, {site.state_name}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-lg text-xs font-medium border whitespace-nowrap ${getStatusColor(site.status)}`}>
          {site.status?.charAt(0).toUpperCase() + site.status?.slice(1) || 'Draft'}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Platform:</span>
          <span className="font-medium capitalize">{site.platform || '—'}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Created:</span>
          <span className="font-medium">
            {site.created_at ? new Date(site.created_at).toLocaleDateString() : '—'}
          </span>
        </div>
      </div>

      {site.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-red-600"><strong>Error:</strong> {site.error}</p>
        </div>
      )}

      {operationStates[site.id] && (
        <div className="flex items-center space-x-2 mb-4 text-sm text-blue-600 bg-blue-50 rounded-lg p-2">
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="capitalize">{operationStates[site.id]}...</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => navigate(`/edit-site/${site.id}`)}
          className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors"
        >
          Edit
        </button>
        
        {site.deployed_url && (
          <a
            href={site.deployed_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg transition-colors"
          >
            View
          </a>
        )}

        {site.ai_content && (
          <button
            onClick={() => handleRegenerate(site.id)}
            disabled={operationStates[site.id]}
            className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          >
            Regenerate
          </button>
        )}

        {(site.status === "deployed" || (site.status === "generated" && site.deployed_url)) && (
          <button
            onClick={() => handleRedeploy(site)}
            disabled={operationStates[site.id]}
            className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          >
            Redeploy
          </button>
        )}

        {site.status === "generated" && !site.deployed_url && site.platform && (
          <button
            onClick={() => handleDeploy(site, site.platform)}
            disabled={operationStates[site.id]}
            className="px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          >
            Deploy
          </button>
        )}

        {/* Delete button */}
        <button
          onClick={() => handleDeleteRequest(site)}
          disabled={operationStates[site.id]}
          className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          title="Delete site"
        >
          Delete
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-64"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Status</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>

            {/* Platform Filter */}
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Platforms</option>
              {platforms.map(platform => (
                <option key={platform} value={platform}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* View Toggle and Actions */}
          <div className="flex items-center space-x-4">
            {/* Bulk actions */}
            {selectedSites.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedSites.length} selected
                </span>
                <button
                  onClick={handleBulkDeleteRequest}
                  className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
                >
                  Delete Selected
                </button>
              </div>
            )}

            {/* Select All Checkbox */}
            {filteredSites.length > 0 && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedSites.length === filteredSites.length}
                  onChange={selectAllSites}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-600">Select All</span>
              </div>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-indigo-50 text-indigo-700 border-r border-indigo-200' 
                    : 'text-gray-500 hover:text-gray-700 border-r border-gray-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-indigo-50 text-indigo-700' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Results Count */}
            <span className="text-sm text-gray-500">
              {filteredSites.length} of {sites.length} sites
            </span>
          </div>
        </div>
      </div>

      {/* Sites Display */}
      {filteredSites.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div className="text-gray-500 text-lg font-medium mb-2">
            {searchTerm || statusFilter !== 'all' || platformFilter !== 'all' 
              ? 'No sites match your filters' 
              : 'No sites yet'
            }
          </div>
          <div className="text-gray-400 text-sm mb-4">
            {searchTerm || statusFilter !== 'all' || platformFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Create your first static site to get started'
            }
          </div>
          {!searchTerm && statusFilter === 'all' && platformFilter === 'all' && (
            <button
              onClick={() => navigate('/create-site')}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
            >
              Create New Site
            </button>
          )}
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
          : 'space-y-4'
        }>
          {filteredSites.map((site) => (
            viewMode === 'grid' ? (
              <SiteCard key={site.id} site={site} />
            ) : (
              <SiteListItem key={site.id} site={site} />
            )
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {deleteTarget.type === 'single' ? 'Delete Site' : 'Delete Multiple Sites'}
                  </h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 mb-2">
                  {deleteTarget.type === 'single' 
                    ? `Are you sure you want to delete "${deleteTarget.siteName}"?`
                    : `Are you sure you want to delete ${deleteTarget.siteIds.length} sites?`
                  }
                </p>
                
                {deleteTarget.type === 'bulk' && deleteTarget.siteNames && (
                  <div className="max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-3 mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Sites to be deleted:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {deleteTarget.siteNames.map((name, index) => (
                        <li key={index}>• {name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                  <p className="text-sm text-red-800">
                    <strong>What will be deleted:</strong>
                  </p>
                  <ul className="text-sm text-red-700 mt-1 space-y-1">
                    <li>• Site data from database</li>
                    <li>• Generated static files</li>
                    <li>• AI content and settings</li>
                  </ul>
                  <p className="text-sm text-red-700 mt-2">
                    <strong>Note:</strong> Deployed sites on Netlify/AWS will remain active but won't be manageable from this dashboard.
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={operationStates.deleting || operationStates.bulk}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  disabled={operationStates.deleting || operationStates.bulk}
                >
                  {(operationStates.deleting || operationStates.bulk) ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>
                      {deleteTarget.type === 'single' ? 'Delete Site' : `Delete ${deleteTarget.siteIds.length} Sites`}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function SiteListItem({ site }) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            {/* Selection checkbox */}
            <input
              type="checkbox"
              checked={selectedSites.includes(site.id)}
              onChange={() => toggleSiteSelection(site.id)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            
            {getStatusIcon(site.status)}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3">
                <h3 className="font-semibold text-gray-900 truncate">
                  {site.service} in {site.city}
                </h3>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(site.status)}`}>
                  {site.status?.charAt(0).toUpperCase() + site.status?.slice(1) || 'Draft'}
                </span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                <span>📍 {site.city}, {site.state_name}</span>
                <span>🛠️ {site.platform || '—'}</span>
                <span>📅 {site.created_at ? new Date(site.created_at).toLocaleDateString() : '—'}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            {operationStates[site.id] && (
              <div className="flex items-center space-x-2 text-sm text-blue-600">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="capitalize">{operationStates[site.id]}...</span>
              </div>
            )}

            <button
              onClick={() => navigate(`/edit-site/${site.id}`)}
              className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors"
            >
              Edit
            </button>
            
            {site.deployed_url && (
              <a
                href={site.deployed_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg transition-colors"
              >
                View
              </a>
            )}

            {site.ai_content && (
              <button
                onClick={() => handleRegenerate(site.id)}
                disabled={operationStates[site.id]}
                className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                Regenerate
              </button>
            )}

            {(site.status === "deployed" || (site.status === "generated" && site.deployed_url)) && (
              <button
                onClick={() => handleRedeploy(site)}
                disabled={operationStates[site.id]}
                className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                Redeploy
              </button>
            )}

            {site.status === "generated" && !site.deployed_url && site.platform && (
              <button
                onClick={() => handleDeploy(site, site.platform)}
                disabled={operationStates[site.id]}
                className="px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                Deploy
              </button>
            )}

            {/* Delete button */}
            <button
              onClick={() => handleDeleteRequest(site)}
              disabled={operationStates[site.id]}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              title="Delete site"
            >
              Delete
            </button>
          </div>
        </div>

        {site.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
            <p className="text-xs text-red-600"><strong>Error:</strong> {site.error}</p>
          </div>
        )}
      </div>
    );
  }
};

export default SiteManager;